import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE } from "@/theme/tokens";
import { SystemFlatSwitch } from "@/ui/Clickable/SystemFlatSwitch";
import { SystemRoundedCheckbox } from "@/ui/Clickable/SystemRoundedCheckbox";
import { SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import { SettingRow } from "@/ui/molecules/SettingRow";
import { SystemSortingButtonWithPopup } from "@/ui/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import type { MultiSortOption, SortingState } from "@/utils/multiColumnSorting";
import { DEFAULT_MONTHLY_SETTINGS, DIMMED_OPACITY, SCHEDULE_EXPORT_TEST_ID } from "./consts";
import { DayColumnWidthField } from "./DayColumnWidthField";
import { MonthlyPageSetupSection } from "./MonthlyPageSetupSection";
import type { MonthlyExportSettingsValue, MonthlySortColumn, MonthlySortEntry, ShiftCellDisplay, ShiftTimeSeparation } from "./types";

/**
 * Only used for `SystemSortingButtonWithPopup`'s `keyof T` typing — nothing here actually sorts a
 * real row array in the browser. The export's real ordering happens server-side
 * (`backend/services/multi_column_sort.py`); this panel just builds the ordered column list.
 */
type MonthlySortableRow = Record<MonthlySortColumn, string>;

const SORTABLE_COLUMNS: MonthlySortColumn[] = ["location", "name", "role"];

/**
 * This panel renders inside `ScheduleExportPopup`'s `MultiSizePopup`, a plain MUI `Dialog` at the
 * framework default z-index (1300). `SystemSortingButtonWithPopup`'s popover has no z-index of its
 * own, so without this override it stacks BELOW the dialog instead of above it. Same fix, same
 * value, as `SystemDataGrid`'s `TOOLBAR_POPUP_Z_INDEX` — one above MUI's default modal layer.
 */
const SORT_POPOVER_Z_INDEX = "1301";

/** `MonthlySortEntry[]` <-> the popup's `SortingState<T>[]`, converted at the component boundary. */
const toSortingState = (entries: MonthlySortEntry[]): SortingState<MonthlySortableRow>[] =>
	entries.map((entry) => ({ sortColumns: [entry.column], sortOrder: entry.direction }));

const fromSortingState = (state: SortingState<MonthlySortableRow>[]): MonthlySortEntry[] =>
	state.map((entry) => ({ column: entry.sortColumns[0], direction: entry.sortOrder }));

/**
 * `SystemSortingButtonWithPopup` round-trips its state through this boundary on every internal
 * change (`useSystemSortingOptionsContentState`'s two effects: local state up via `setSortState`,
 * `initialState` back down into local state). Its own re-sync effect compares `initialState` to its
 * last-sent value by REFERENCE (`Array.prototype.includes`) — but `toSortingState`/`fromSortingState`
 * build brand-new objects on every call, so a same-content round trip still reads as "different"
 * forever, which drove `setSortState` → `onChange` → new props → `setSortState` → ... in an infinite
 * loop (visible as React's "Maximum update depth exceeded"). Comparing by VALUE here, and skipping a
 * no-op `onChange`, starves that effect of a new reference to react to and breaks the cycle.
 */
const sortEntriesEqual = (a: MonthlySortEntry[], b: MonthlySortEntry[]): boolean =>
	a.length === b.length && a.every((entry, i) => entry.column === b[i].column && entry.direction === b[i].direction);

export type MonthlyExportSettingsProps = {
	value: MonthlyExportSettingsValue;
	onChange: <K extends keyof MonthlyExportSettingsValue>(key: K, next: MonthlyExportSettingsValue[K]) => void;
	/**
	 * Set EVERY day column to one pixel width at once.
	 *
	 * A separate callback rather than a settings key: the widths live in `columnWidths`, which is keyed
	 * by absolute column index and so depends on the selected date range and whether the hire-date
	 * column is showing — neither of which this panel knows. The owner resolves the indices.
	 */
	onDayColumnWidth: (widthPx: number) => void;
	disabled?: boolean;
};

/** Checkbox options, in panel order. `printGaps` is separate: it is the one with an interlock. */
const PLAIN_CHECKBOXES: Array<{
	key: "showHireDate" | "recurringOnly" | "hideAgency" | "showPtoSheet";
	labelKey: string;
	testId: string;
	/** Only where the option has a consequence the label cannot carry. */
	tooltipKey?: string;
}> = [
	// `testId` is spelled out rather than derived from `key`: the convention in
	// context/standards/testing.md is kebab-case, and the settings keys are camelCase.
	{ key: "showHireDate", labelKey: "download.show.hire.date", testId: "show-hire-date" },
	{ key: "recurringOnly", labelKey: "download.recurring.only", testId: "recurring-only" },
	// The tooltip says what still happens anyway: a slot agency covers is filled, so it prints no gap row.
	{ key: "hideAgency", labelKey: "download.hide.agency", testId: "hide-agency", tooltipKey: "download.hide.agency.tooltip" },
	{ key: "showPtoSheet", labelKey: "printout.show.pto", testId: "show-pto-sheet" },
];

/**
 * The monthly-only export options — the orchestration shell.
 *
 * Page setup and the day-column-width command live in siblings (`MonthlyPageSetupSection`,
 * `DayColumnWidthField`); what stays here is the ordering and the one cross-field interlock.
 */
export const MonthlyExportSettings: FC<MonthlyExportSettingsProps> = ({ value, onChange, onDayColumnWidth, disabled }) => {
	const { t } = useTranslation();

	// Gaps are computed per shift-time window by definition, so flat mode has no window to attribute one
	// to. Disabled AND forced off, so the impossible combination cannot be produced from the panel —
	// including for a stored document saved before the mode was switched.
	const gapsUnavailable = value.shiftTimeSeparation === "none";

	// One option per sortable column — reordering/adding/removing entries in the popup is what makes
	// Location/Name/Role a real multi-column, per-direction sort instead of the old single dropdown.
	const sortingOptions: MultiSortOption<MonthlySortableRow>[] = SORTABLE_COLUMNS.map((column) => ({
		label: t(column),
		sortColumns: [column],
	}));

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
			<SystemDropdown
				data-testid={`${SCHEDULE_EXPORT_TEST_ID}-shift-time-separation`}
				label={t("download.separate.shift.times")}
				items={[
					{ label: t("download.separate.shift.times.sections"), value: "sections" satisfies ShiftTimeSeparation },
					{ label: t("download.separate.shift.times.tabs"), value: "tabs" satisfies ShiftTimeSeparation },
					{ label: t("download.separate.shift.times.none"), value: "none" satisfies ShiftTimeSeparation },
				]}
				selectedItem={value.shiftTimeSeparation}
				// `SystemDropdown` hands back the raw item value as a string; the three items above are the
				// only values it can produce, and all three are `ShiftTimeSeparation`.
				onChange={(next) => onChange("shiftTimeSeparation", next as ShiftTimeSeparation)}
				fullWidth
				disabled={disabled}
			/>
			<SettingRow
				dataTestId={`${SCHEDULE_EXPORT_TEST_ID}-shift-cell-display`}
				label={t("download.shift.cell.display")}
				labelFontSize={FONT_SIZE.SECONDARY}
				// Stacked, not beside the label: three intrinsic-width options cannot shrink, so on one line a
				// long translation ("Visualización de la celda de turno" + "Marcar como trabajado") crushes the
				// label to nothing and pushes the switch past the 500px panel. Measured at 1536px: 525px on one
				// line vs exactly 500px stacked, still one line each.
				stacked
				control={
					<SystemFlatSwitch<ShiftCellDisplay>
						data-testid={`${SCHEDULE_EXPORT_TEST_ID}-shift-cell-display-switch`}
						options={[
							// Per-option, because that is the only `disabled` the switch has — a wrapper's
							// `pointerEvents: none` stops the mouse but leaves the buttons keyboard-activatable.
							// Same pattern as the orientation switch in `MonthlyPageSetupSection`.
							{ label: t("download.shift.cell.display.time"), value: "time", disabled },
							{ label: t("download.shift.cell.display.hours"), value: "hours", disabled },
							{ label: t("download.shift.cell.display.mark"), value: "mark", disabled },
						]}
						activeIndexOrValue={value.shiftCellDisplay}
						onSwitch={(option) => onChange("shiftCellDisplay", option.value)}
					/>
				}
			/>
			<SettingRow
				dataTestId={`${SCHEDULE_EXPORT_TEST_ID}-sort-by`}
				label={t("download.sort.by")}
				control={
					// `SystemSortingButtonWithPopup` has no `disabled` prop of its own — every other caller in
					// the app renders it unconditionally enabled — so the panel's own `disabled` locks it the
					// same way `PrintoutSettingsPanel` locks a section it cannot forward `disabled` into.
					<Box sx={{ opacity: disabled ? DIMMED_OPACITY : 1, pointerEvents: disabled ? "none" : "auto" }}>
						<SystemSortingButtonWithPopup<MonthlySortableRow>
							sortingOptions={sortingOptions}
							initialState={toSortingState(value.sortEntries)}
							setSortState={(next) => {
								const resolved = typeof next === "function" ? next(toSortingState(value.sortEntries)) : next;
								const nextSortEntries = fromSortingState(resolved);
								if (sortEntriesEqual(nextSortEntries, value.sortEntries)) return;
								onChange("sortEntries", nextSortEntries);
							}}
							defaultSortState={toSortingState(DEFAULT_MONTHLY_SETTINGS.sortEntries)}
							// The one caller that keeps the glyph: `SettingRow` already renders "Sort by" beside it, so
							// the button's own "Sort" would say it twice. That text is a sibling element rather than a
							// real <label> though, so nothing names the button programmatically — hence the aria label.
							iconOnly
							triggerAriaLabel={t("download.sort.by")}
							// The export has no meaning for "no sort": `sortEntries` is `min_length=1` server-side
							// (`models/authn.py`), so an empty list 422s the preview and the download. Keeping one
							// row in the popup is what stops the UI from producing a request the API rejects.
							minEntries={1}
							overridePopoverProps={{ containerStyle: { zIndex: SORT_POPOVER_Z_INDEX } }}
						/>
					</Box>
				}
			/>

			{/* Always one column: the settings sit in a 320px panel, and the minimized card is only 450px. */}
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
				<MonthlyPageSetupSection value={value} onChange={onChange} disabled={disabled} />
				<SystemRoundedCheckbox
					data-testid={`${SCHEDULE_EXPORT_TEST_ID}-print-gaps`}
					// Forced off rather than merely disabled, so what the panel shows is what the export does.
					checked={gapsUnavailable ? false : value.printGaps}
					onChange={(checked) => onChange("printGaps", checked)}
					label={t("download.include.gaps")}
					disabled={disabled || gapsUnavailable}
					tooltip={gapsUnavailable ? t("download.include.gaps.needs.shift.times") : undefined}
				/>
				{PLAIN_CHECKBOXES.map(({ key, labelKey, testId, tooltipKey }) => (
					<SystemRoundedCheckbox
						key={key}
						data-testid={`${SCHEDULE_EXPORT_TEST_ID}-${testId}`}
						checked={value[key]}
						onChange={(checked) => onChange(key, checked)}
						label={t(labelKey)}
						disabled={disabled}
						tooltip={tooltipKey ? t(tooltipKey) : undefined}
					/>
				))}
			</Box>

			<DayColumnWidthField onApply={onDayColumnWidth} disabled={disabled} />
		</Box>
	);
};
